import {Button, Card, Modal, Space, Typography} from 'antd';
import {
  ChargeFieldsFragment,
  MessageChannel,
  PaymentEmailType,
  SendPaymentMessageInput,
  usePreviewPaymentEmailQuery,
  useSendPaymentReceiptMutation
} from 'api/generated';
import {AnchorLink, PhoneNumberInput, Spacer} from 'components';
import {HtmlPreview} from 'components/HtmlPreview';
import {Formik} from 'formik';
import {Form, FormItem, Input, Select} from 'formik-antd';
import {FormikHelpers} from 'formik/dist/types';
import {useCountries} from 'hooks/useCountries';
import {useHostedPaymentDomain} from 'hooks/useHostedPaymentDomain';
import ISO6391 from 'iso-639-1';
import {omitEmpty} from 'lib/omitEmpty';
import {normalizePhone} from 'lib/utils';
import {validatePhone} from 'lib/validation';
import React, {useMemo, useState} from 'react';
import {config} from 'stage.config';
import styled from 'styled-components';
import {useDebounce} from 'use-debounce';
import * as yup from 'yup';

type Props = {payment: ChargeFieldsFragment};

type FormValues = SendPaymentMessageInput;

const {Text} = Typography;
const {Option} = Select;

const Preview = styled(HtmlPreview)`
  width: 100%;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
`;

const channelOptions = [
  {
    label: 'Email',
    value: MessageChannel.EMAIL
  },
  {
    label: 'WhatsApp',
    value: MessageChannel.WHATSAPP
  },
  {
    label: 'SMS',
    value: MessageChannel.SMS
  }
];

export const SendPaymentReceipt: React.FC<Props> = ({payment}) => {
  const [isOpen, setOpen] = useState(false);
  const [skip] = useDebounce(!isOpen, 500);
  const [sendPaymentReceipt] = useSendPaymentReceiptMutation();
  const {currentCountry} = useCountries();
  const hostedPaymentDomain = useHostedPaymentDomain(!payment, payment.accountId);
  const [language, setLanguage] = useState('en');

  const {data} = usePreviewPaymentEmailQuery({
    skip,
    variables: {
      accountId: payment.accountId,
      paymentId: payment.id,
      emailType: PaymentEmailType.RECEIPT,
      language
    }
  });

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        channel: yup.string().required(),
        customerEmail: yup.string().when('channel', {
          is: MessageChannel.EMAIL,
          then: yup.string().email('Invalid email format').required('Email is required')
        }),
        customerPhone: yup.string().when('channel', {
          is: MessageChannel.EMAIL,
          then: yup.string().optional(),
          otherwise: yup
            .string()
            .test('phone', 'Invalid phone format', validatePhone)
            .required('Phone is required')
        })
      }),
    []
  );

  if (!payment) return null;

  const initialValues: FormValues = omitEmpty({
    accountId: payment.accountId,
    channel:
      payment.customer?.phone && !payment.customer.email
        ? MessageChannel.WHATSAPP
        : MessageChannel.EMAIL,
    paymentId: payment.id,
    customerEmail: payment.customer?.email,
    customerPhone: normalizePhone(payment.customer?.phone, payment.sessionDetails?.countryCode),
    language
  });

  const handleSubmit = async (
    input: FormValues,
    {setSubmitting, resetForm}: FormikHelpers<FormValues>
  ) => {
    await sendPaymentReceipt({variables: {input}});
    setSubmitting(false);
    setOpen(false);
    setTimeout(resetForm, 100);
  };

  const receiptUrl = `https://${hostedPaymentDomain}/payments/${payment.id}/receipt?p=1&locale=${language}`;

  return (
    <>
      <Space>
        <Button href={receiptUrl} target="_blank">
          View receipt
        </Button>
        <Button onClick={() => setOpen(true)}>Send receipt</Button>
      </Space>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}>
        {({submitForm, isSubmitting, resetForm, values}) => (
          <Modal
            title={'Send receipt'}
            okText={'Send receipt'}
            cancelText={'Cancel'}
            open={isOpen}
            width={600}
            confirmLoading={isSubmitting}
            onOk={submitForm}
            afterClose={resetForm}
            onCancel={() => {
              setOpen(false);
            }}>
            <Form layout="vertical">
              <FormItem name="channel" label={'Delivery channel'}>
                <Select name="channel" options={channelOptions} />
              </FormItem>
              {values.channel === MessageChannel.EMAIL ? (
                <FormItem name="customerEmail" label={'Send to'}>
                  <Input name="customerEmail" placeholder="email@example.com" />
                </FormItem>
              ) : (
                <FormItem name="customerPhone" label={'Send to'}>
                  <PhoneNumberInput name="customerPhone" countryCode={currentCountry?.code} />
                </FormItem>
              )}
              <FormItem name="language" label={'Customer language'}>
                <Select name="language" onChange={(value) => setLanguage(value)}>
                  {config.supportedLanguages.map((code) => (
                    <Option key={code} value={code}>
                      {ISO6391.getNativeName(code)}
                    </Option>
                  ))}
                </Select>
              </FormItem>
              {values.channel === MessageChannel.EMAIL && (
                <FormItem
                  name="preview"
                  label={
                    <>
                      <span>Email preview</span>
                      <Spacer />
                      <Text copyable={{text: receiptUrl}}>
                        <AnchorLink href={receiptUrl} external target="_blank">
                          receipt permalink
                        </AnchorLink>
                      </Text>
                    </>
                  }
                  style={{marginBottom: 0}}>
                  {data ? (
                    <Preview html={data?.previewPaymentEmail || ''} />
                  ) : (
                    <Card loading style={{height: 400}} />
                  )}
                </FormItem>
              )}
              <input type="submit" value="submit" hidden />
            </Form>
          </Modal>
        )}
      </Formik>
    </>
  );
};
